import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  Shield, 
  Trash2, 
  Loader2, 
  ArrowLeft,
  Clock,
  Settings,
  Key,
  Database,
  Cloud,
  Save,
  Check,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('users'); // 'users' or 'settings'

  // Settings state
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [airtableClientId, setAirtableClientId] = useState('');
  const [airtableClientSecret, setAirtableClientSecret] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [mailgunApiKey, setMailgunApiKey] = useState('');
  const [mailgunDomain, setMailgunDomain] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState({});
  const [airtableOAuthStatus, setAirtableOAuthStatus] = useState({ connected: false });

  useEffect(() => {
    loadUsers();
    loadSettings();
    loadAirtableOAuthStatus();
    
    // Handle OAuth callback query params
    const params = new URLSearchParams(window.location.search);
    if (params.get('airtable_connected') === 'true') {
      toast.success('Airtable connected successfully!');
      // Clean URL
      window.history.replaceState({}, '', '/admin');
      setActiveSection('settings');
    } else if (params.get('airtable_error') === 'true') {
      toast.error('Failed to connect Airtable. Please try again.');
      window.history.replaceState({}, '', '/admin');
      setActiveSection('settings');
    }
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      await axios.post(`${API}/users/approve`, { user_id: userId });
      toast.success('User approved');
      loadUsers();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve user');
    }
  };

  const makeAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to make this user an admin?')) {
      return;
    }

    try {
      await axios.post(`${API}/users/make-admin`, { user_id: userId });
      toast.success('User promoted to admin');
      loadUsers();
    } catch (error) {
      console.error('Make admin error:', error);
      toast.error('Failed to make user admin');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted');
      loadUsers();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettingsStatus(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const saveSettings = async () => {
    setIsSavingSettings(true);
    try {
      await axios.post(`${API}/settings/save`, {
        claude_api_key: claudeApiKey,
        gemini_api_key: geminiApiKey,
        airtable_client_id: airtableClientId,
        airtable_client_secret: airtableClientSecret,
        google_client_id: googleClientId,
        google_client_secret: googleClientSecret,
        mailgun_api_key: mailgunApiKey,
        mailgun_domain: mailgunDomain
      });

      toast.success('Settings saved successfully!');
      loadSettings();
      
      // Clear sensitive fields
      setClaudeApiKey('');
      setGeminiApiKey('');
      setAirtableClientId('');
      setAirtableClientSecret('');
      setGoogleClientId('');
      setGoogleClientSecret('');
      setMailgunApiKey('');
      setMailgunDomain('');
    } catch (error) {
      console.error('Save settings error:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const loadAirtableOAuthStatus = async () => {
    try {
      const response = await axios.get(`${API}/airtable-oauth/status`);
      setAirtableOAuthStatus(response.data || { connected: false });
    } catch (error) {
      // Silently fail - this is expected if user isn't authenticated or isn't admin
      console.log('Airtable OAuth status unavailable');
      setAirtableOAuthStatus({ connected: false });
    }
  };

  const connectAirtable = async () => {
    try {
      const response = await axios.get(`${API}/airtable-oauth/connect`);
      if (response.data?.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else {
        toast.error('Failed to get Airtable authorization URL');
      }
    } catch (error) {
      console.error('Airtable connect error:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect Airtable');
    }
  };

  const disconnectAirtable = async () => {
    if (!window.confirm('Are you sure you want to disconnect Airtable?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/airtable-oauth/disconnect`);
      toast.success('Airtable disconnected');
      loadAirtableOAuthStatus();
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error('Failed to disconnect Airtable');
    }
  };

  const pendingUsers = users.filter(u => !u.approved);
  const approvedUsers = users.filter(u => u.approved);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a]">
      {/* Header */}
      <header className="glass-morphism sticky top-0 z-50 px-4 md:px-8 py-4 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-white/60 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="font-mono text-[#00d4aa] text-xs uppercase tracking-wider mb-1">
                ADMIN PANEL
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-white">
                Admin Dashboard
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:block text-sm text-white/60">
              <span className="text-white font-semibold">{user?.name}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 border-b border-white/10">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveSection('users')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeSection === 'users'
                ? 'bg-[#00d4aa] text-black'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            User Management
          </button>
          <button
            onClick={() => setActiveSection('settings')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeSection === 'settings'
                ? 'bg-[#00d4aa] text-black'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Global Settings
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {loading && activeSection === 'users' ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
          </div>
        ) : activeSection === 'users' ? (
          <div className="space-y-8">
            {/* Pending Approvals */}
            {pendingUsers.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-yellow-400" />
                  Pending Approvals ({pendingUsers.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pendingUsers.map((u) => (
                    <Card key={u.id} className="card border-yellow-500/30">
                      <CardContent className="pt-6">
                        <div className="space-y-3">
                          <div>
                            <h3 className="font-semibold text-white text-lg">{u.name}</h3>
                            <p className="text-sm text-white/60">{u.email}</p>
                          </div>

                          <div className="text-xs text-white/40">
                            Joined: {new Date(u.created_at).toLocaleDateString()}
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => approveUser(u.id)}
                              className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteUser(u.id)}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* All Users */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-[#00d4aa]" />
                All Users ({approvedUsers.length})
              </h2>
              <div className="grid grid-cols-1 gap-3">
                {approvedUsers.map((u) => (
                  <Card key={u.id} className="card">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-10 h-10 rounded-full bg-[#00d4aa]/20 flex items-center justify-center">
                            <span className="text-[#00d4aa] font-bold">
                              {u.name.charAt(0).toUpperCase()}
                            </span>
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-white">{u.name}</h3>
                              {u.role === 'admin' && (
                                <span className="px-2 py-0.5 bg-[#00d4aa]/20 text-[#00d4aa] rounded text-xs font-semibold">
                                  <Shield className="w-3 h-3 inline mr-1" />
                                  ADMIN
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-white/60">{u.email}</p>
                          </div>

                          <div className="hidden md:block text-xs text-white/40">
                            Joined: {new Date(u.created_at).toLocaleDateString()}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {u.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => makeAdmin(u.id)}
                              className="border-[#00d4aa]/30 text-[#00d4aa] hover:bg-[#00d4aa]/10"
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              Make Admin
                            </Button>
                          )}
                          
                          {u.id !== user.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteUser(u.id)}
                              className="text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-white text-sm">Total Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-white">{users.length}</p>
                </CardContent>
              </Card>

              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-white text-sm">Admins</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#00d4aa]">
                    {users.filter(u => u.role === 'admin').length}
                  </p>
                </CardContent>
              </Card>

              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-white text-sm">Pending Approval</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-yellow-400">
                    {pendingUsers.length}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          // Settings Section
          <div className="space-y-6 animate-fadeIn">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Global Settings</h2>
              <p className="text-white/60">Manage API keys and integrations for all users</p>
            </div>

            {/* Claude AI Settings */}
            <Card className="card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Key className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Claude AI</CardTitle>
                    <CardDescription className="text-white/60">
                      Required for content generation
                    </CardDescription>
                  </div>
                  {settingsStatus.hasClaude && (
                    <div className="ml-auto">
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                        <Check className="w-4 h-4" />
                        Configured
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="claude-key" className="text-white/80">
                    Claude API Key
                  </Label>
                  <Input
                    id="claude-key"
                    type="password"
                    value={claudeApiKey}
                    onChange={(e) => setClaudeApiKey(e.target.value)}
                    placeholder="sk-ant-api..."
                    className="bg-white/5 border-white/10 text-white mt-2"
                  />
                  <p className="text-xs text-white/40 mt-2">
                    Get your API key from{' '}
                    <a
                      href="https://console.anthropic.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00d4aa] hover:underline"
                    >
                      console.anthropic.com
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Gemini AI Settings */}
            <Card className="card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <Key className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Gemini AI</CardTitle>
                    <CardDescription className="text-white/60">
                      Required for image generation
                    </CardDescription>
                  </div>
                  {settingsStatus.hasGemini && (
                    <div className="ml-auto">
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                        <Check className="w-4 h-4" />
                        Configured
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="gemini-key" className="text-white/80">
                    Gemini API Key
                  </Label>
                  <Input
                    id="gemini-key"
                    type="password"
                    value={geminiApiKey}
                    onChange={(e) => setGeminiApiKey(e.target.value)}
                    placeholder="AIza..."
                    className="bg-white/5 border-white/10 text-white mt-2"
                  />
                  <p className="text-xs text-white/40 mt-2">
                    Get your API key from{' '}
                    <a
                      href="https://aistudio.google.com/app/apikey"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#00d4aa] hover:underline"
                    >
                      Google AI Studio
                    </a>
                    . Required for image generation.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Google Drive Settings */}
            <Card className="card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Cloud className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Google Drive</CardTitle>
                    <CardDescription className="text-white/60">
                      OAuth credentials for document loading
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="google-client-id" className="text-white/80">
                      Google Client ID
                    </Label>
                    <Input
                      id="google-client-id"
                      type="text"
                      value={googleClientId}
                      onChange={(e) => setGoogleClientId(e.target.value)}
                      placeholder="xxxxx.apps.googleusercontent.com"
                      className="bg-white/5 border-white/10 text-white mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="google-client-secret" className="text-white/80">
                      Google Client Secret
                    </Label>
                    <Input
                      id="google-client-secret"
                      type="password"
                      value={googleClientSecret}
                      onChange={(e) => setGoogleClientSecret(e.target.value)}
                      placeholder="GOCSPX-..."
                      className="bg-white/5 border-white/10 text-white mt-2"
                    />
                  </div>
                </div>
                <p className="text-xs text-white/40">
                  Create OAuth credentials at{' '}
                  <a
                    href="https://console.cloud.google.com/apis/credentials"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00d4aa] hover:underline"
                  >
                    Google Cloud Console
                  </a>
                </p>
              </CardContent>
            </Card>

            {/* Airtable OAuth Settings */}
            <Card className="card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#00d4aa]/20 rounded-lg">
                    <Database className="w-5 h-5 text-[#00d4aa]" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Airtable</CardTitle>
                    <CardDescription className="text-white/60">
                      Project storage and management via OAuth
                    </CardDescription>
                  </div>
                  {airtableOAuthStatus?.connected && (
                    <div className="ml-auto">
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                        <Check className="w-4 h-4" />
                        Connected
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* OAuth Credentials (needed before connecting) */}
                {!airtableOAuthStatus?.connected && (
                  <>
                    <p className="text-sm text-white/60 mb-4">
                      First, enter your Airtable OAuth credentials below and save settings. Then click &quot;Connect Airtable&quot; to authorize.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="airtable-client-id" className="text-white/80">
                          Client ID
                        </Label>
                        <Input
                          id="airtable-client-id"
                          type="text"
                          value={airtableClientId}
                          onChange={(e) => setAirtableClientId(e.target.value)}
                          placeholder="Enter Airtable Client ID"
                          className="bg-white/5 border-white/10 text-white mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="airtable-client-secret" className="text-white/80">
                          Client Secret
                        </Label>
                        <Input
                          id="airtable-client-secret"
                          type="password"
                          value={airtableClientSecret}
                          onChange={(e) => setAirtableClientSecret(e.target.value)}
                          placeholder="Enter Airtable Client Secret"
                          className="bg-white/5 border-white/10 text-white mt-2"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-white/40">
                      Create OAuth credentials at{' '}
                      <a
                        href="https://airtable.com/create/oauth"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00d4aa] hover:underline"
                      >
                        airtable.com/create/oauth
                      </a>
                    </p>
                  </>
                )}
                
                {/* OAuth Connect/Disconnect Button */}
                <div className="pt-2">
                  {airtableOAuthStatus?.connected ? (
                    <div className="space-y-3">
                      <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                        <div className="flex items-center gap-2 text-green-400 mb-2">
                          <Check className="w-4 h-4" />
                          <span className="font-semibold">Airtable Connected</span>
                        </div>
                        {airtableOAuthStatus?.expires_at && (
                          <p className="text-xs text-white/40">
                            Token expires: {new Date(airtableOAuthStatus.expires_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={disconnectAirtable}
                        variant="outline"
                        className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        Disconnect Airtable
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={connectAirtable}
                      disabled={!settingsStatus?.hasAirtable}
                      className="w-full bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
                    >
                      <Cloud className="w-4 h-4 mr-2" />
                      Connect Airtable
                    </Button>
                  )}
                  {!settingsStatus?.hasAirtable && !airtableOAuthStatus?.connected && (
                    <p className="text-xs text-yellow-400 mt-2 text-center">
                      Please enter Client ID & Secret and save settings first
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Mailgun Email Settings */}
            <Card className="card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <Mail className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Email Notifications (Mailgun)</CardTitle>
                    <CardDescription className="text-white/60">
                      Send email alerts when campaigns are submitted
                    </CardDescription>
                  </div>
                  {settingsStatus.hasMailgun && (
                    <div className="ml-auto">
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                        <Check className="w-4 h-4" />
                        Configured
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mailgun-api-key" className="text-white/80">
                      Mailgun API Key
                    </Label>
                    <Input
                      id="mailgun-api-key"
                      type="password"
                      value={mailgunApiKey}
                      onChange={(e) => setMailgunApiKey(e.target.value)}
                      placeholder={settingsStatus.hasMailgun ? '••••••••••' : 'key-xxxxxxxxxxxxxxxx'}
                      className="bg-white/5 border-white/10 text-white mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mailgun-domain" className="text-white/80">
                      Mailgun Domain
                    </Label>
                    <Input
                      id="mailgun-domain"
                      type="text"
                      value={mailgunDomain}
                      onChange={(e) => setMailgunDomain(e.target.value)}
                      placeholder={settingsStatus.mailgunDomain || 'mail.yourdomain.com'}
                      className="bg-white/5 border-white/10 text-white mt-2"
                    />
                  </div>
                </div>
                <p className="text-xs text-white/40">
                  Get your API key from{' '}
                  <a
                    href="https://app.mailgun.com/app/account/security/api_keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00d4aa] hover:underline"
                  >
                    Mailgun Dashboard
                  </a>
                  . Use a verified sending domain.
                </p>
                {settingsStatus.hasMailgun && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-400">
                      ✓ Email notifications enabled. Admins will receive alerts when campaigns are submitted.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              onClick={saveSettings}
              disabled={isSavingSettings}
              className="w-full h-12 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold text-lg"
            >
              {isSavingSettings ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save All Settings
                </>
              )}
            </Button>

            {/* Info Box */}
            <div className="p-6 bg-white/5 rounded-lg text-center border border-white/10">
              <p className="text-white/60 text-sm">
                🔒 All API keys are securely stored. These settings apply globally to all users.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminPanel;
